import {
    collectDescendantFolderIdsFromFlat,
    deriveSourceFlags,
    selectedFolderIds,
    SCOPE_SENTINEL,
    workstreamOptionsFromList,
} from './aiWardenScope';

export const CUSTOM_SOURCE_KEYS = ['documents', 'irl', 'observations'];

export const CUSTOM_SOURCE_LABELS = {
    documents: 'Documents (VDR)',
    irl: 'IRL Responses',
    observations: 'Observations',
};

export function createEmptyCustomContext() {
    return {
        documents: {
            enabled: true,
            workstreams: [],
            folders: [],
            selectedFileIds: [],
        },
        irl: {
            enabled: true,
            workstreams: [],
            tags: [],
            selectedIrlIds: [],
        },
        observations: {
            enabled: true,
            workstreams: [],
            tags: [],
            selectedObservationIds: [],
        },
    };
}

export function cloneCustomContext(config) {
    if (!config) return createEmptyCustomContext();
    return JSON.parse(JSON.stringify(config));
}

export function getCustomSourceWorkstreamIds(config, sourceKey, allWorkstreamIds = []) {
    const source = config?.[sourceKey];
    if (!source?.enabled) return [];
    const values = (source.workstreams || []).map(o => o?.value).filter(Boolean);
    if (!values.length) return [];
    if (values.includes(SCOPE_SENTINEL.ALL)) {
        return (allWorkstreamIds || []).map(String);
    }
    return values.filter(v => v !== SCOPE_SENTINEL.ALL).map(String);
}

export function getUnionCustomWorkstreamIds(config, allWorkstreamIds = []) {
    const ids = CUSTOM_SOURCE_KEYS.flatMap(key =>
        getCustomSourceWorkstreamIds(config, key, allWorkstreamIds),
    );
    return [...new Set(ids)];
}

/** Expand ALL workstream sentinels to concrete options before persisting. */
export function materializeCustomContextWorkstreams(config, workstreams = []) {
    const next = cloneCustomContext(config);
    const allOptions = workstreamOptionsFromList(workstreams);
    for (const key of CUSTOM_SOURCE_KEYS) {
        const selected = next[key]?.workstreams || [];
        if (selected.some(o => o?.value === SCOPE_SENTINEL.ALL)) {
            next[key].workstreams = allOptions;
        }
    }
    return next;
}

export function deriveCustomSourceFlags(config) {
    const irlDisabled =
        !config?.irl?.enabled || (config.irl.tags || []).some(o => o?.value === SCOPE_SENTINEL.NONE);
    const obsDisabled =
        !config?.observations?.enabled ||
        (config.observations.tags || []).some(o => o?.value === SCOPE_SENTINEL.NONE);

    const base = deriveSourceFlags(
        irlDisabled ? [{ value: SCOPE_SENTINEL.NONE }] : config.irl.tags || [],
        obsDisabled ? [{ value: SCOPE_SENTINEL.NONE }] : config.observations.tags || [],
    );

    return {
        includeVdr: Boolean(config?.documents?.enabled),
        includeIrl: Boolean(config?.irl?.enabled && base.includeIrl),
        includeObservations: Boolean(config?.observations?.enabled && base.includeObservations),
        irlTags: config?.irl?.enabled ? base.irlTags : undefined,
        observationTags: config?.observations?.enabled ? base.observationTags : undefined,
    };
}

export function filterRowsByTags(rows, tagField, selectedTagOptions = []) {
    const tags = (selectedTagOptions || [])
        .map(o => o?.value)
        .filter(v => v && v !== SCOPE_SENTINEL.NONE && v !== SCOPE_SENTINEL.ALL);
    if (!tags.length) return rows;
    const allowed = new Set(tags.map(t => String(t).toLowerCase()));
    return (rows || []).filter(row => {
        const raw = row?.[tagField] ?? row?.tag ?? row?.tag_repr;
        return allowed.has(String(raw || '').toLowerCase());
    });
}

export function filterDocumentsByFolders(files, folders, selectedFolderOptions, flatFolders = []) {
    const folderNums = selectedFolderIds(selectedFolderOptions);
    if (!folderNums?.length) return files || [];
    const allowed = collectDescendantFolderIdsFromFlat(flatFolders, folderNums);
    if (!allowed.size) return files || [];
    return (files || []).filter(f => allowed.has(Number(f.ws_folder_id)));
}

function applyItemSelection(items, selectedIds, getId = item => item.id) {
    const selected = (selectedIds || []).map(String).filter(Boolean);
    if (selected.includes(SCOPE_SENTINEL.NONE)) return [];
    if (!selected.length) return items || [];
    const selectedSet = new Set(selected.filter(id => id !== SCOPE_SENTINEL.NONE));
    return (items || []).filter(item => selectedSet.has(String(getId(item))));
}

export function listDocumentsInCustomScope(config, files, flatFolders = [], allWorkstreamIds = []) {
    if (!config?.documents?.enabled) return [];
    const wsIds = new Set(
        getCustomSourceWorkstreamIds(config, 'documents', allWorkstreamIds).map(String),
    );
    let scoped = (files || []).filter(f => wsIds.has(String(f.workstream_id)));
    return filterDocumentsByFolders(scoped, flatFolders, config.documents.folders, flatFolders);
}

export function resolveDocumentsInScope(config, files, flatFolders = [], allWorkstreamIds = []) {
    return applyItemSelection(
        listDocumentsInCustomScope(config, files, flatFolders, allWorkstreamIds),
        config.documents.selectedFileIds,
    );
}

export function listIrlInCustomScope(config, rows = [], allWorkstreamIds = []) {
    if (!config?.irl?.enabled) return [];
    const wsIds = new Set(getCustomSourceWorkstreamIds(config, 'irl', allWorkstreamIds).map(String));
    let scoped = (rows || []).filter(row => wsIds.has(String(row.workstream_id)));
    return filterRowsByTags(scoped, 'tag', config.irl.tags);
}

export function resolveIrlInScope(config, rows = [], allWorkstreamIds = []) {
    return applyItemSelection(
        listIrlInCustomScope(config, rows, allWorkstreamIds),
        config.irl.selectedIrlIds,
    );
}

export function listObservationsInCustomScope(config, rows = []) {
    if (!config?.observations?.enabled) return [];
    return filterRowsByTags(rows || [], 'tag_repr', config.observations.tags);
}

export function resolveObservationsInScope(config, rows = []) {
    return applyItemSelection(
        listObservationsInCustomScope(config, rows),
        config.observations.selectedObservationIds,
    );
}

export function formatVdrDocumentLabel(doc) {
    if (doc?.document_name) return doc.document_name;
    if (doc?.name) return doc.name;
    if (doc?.metadata_storage_name) return doc.metadata_storage_name;
    if (doc?.item_num && doc?.ws_folder) {
        return `${doc.item_num} ${doc.ws_folder}`;
    }
    return `File ${doc?.id ?? ''}`.trim();
}

export function formatIrlRowLabel(row) {
    const parts = [row?.tag, row?.prefix].filter(Boolean);
    return parts.join(' · ') || `IRL ${row?.id ?? ''}`.trim();
}

export function formatObservationRowLabel(row) {
    const parts = [row?.workstream_repr, row?.tag_repr].filter(Boolean);
    return parts.join(' · ') || `Observation ${row?.id ?? ''}`.trim();
}

export function effectiveSelectedIds(selectedIds, scopedItems, getId) {
    const selected = (selectedIds || []).map(String);
    if (selected.includes(SCOPE_SENTINEL.NONE)) return [];
    if (!selected.length) {
        return (scopedItems || []).map(item => String(getId(item)));
    }
    return selected.filter(id => id !== SCOPE_SENTINEL.NONE);
}

export function resolveCustomContextScope(config, allWorkstreamIds = [], workstreams = []) {
    const sourceFlags = deriveCustomSourceFlags(config);
    const vdrWorkstreamIds = getCustomSourceWorkstreamIds(config, 'documents', allWorkstreamIds)
        .map(Number)
        .filter(Number.isFinite);
    const irlWorkstreamIds = getCustomSourceWorkstreamIds(config, 'irl', allWorkstreamIds)
        .map(Number)
        .filter(Number.isFinite);
    const obsWorkstreamIds = getCustomSourceWorkstreamIds(config, 'observations', allWorkstreamIds);
    const unionIds = [
        ...new Set([
            ...vdrWorkstreamIds,
            ...irlWorkstreamIds,
            ...obsWorkstreamIds.map(Number).filter(Number.isFinite),
        ]),
    ];
    const wsById = new Map((workstreams || []).map(w => [String(w.id), w]));
    const observationWorkstreamReprs = obsWorkstreamIds
        .map(id => wsById.get(String(id))?.name)
        .filter(Boolean);

    const folderIds = config?.documents?.enabled ? selectedFolderIds(config.documents.folders) : null;
    const rawFileIds = config?.documents?.selectedFileIds || [];
    const noneSelected = rawFileIds.some(id => String(id) === SCOPE_SENTINEL.NONE);
    const fileIds = !config?.documents?.enabled
        ? null
        : noneSelected
          ? []
          : rawFileIds.map(Number).filter(Number.isFinite);

    return {
        type: 'custom',
        workstreamIds: unionIds,
        vdrWorkstreamIds: config?.documents?.enabled ? vdrWorkstreamIds : [],
        irlWorkstreamIds: config?.irl?.enabled ? irlWorkstreamIds : [],
        observationWorkstreamReprs: config?.observations?.enabled ? observationWorkstreamReprs : [],
        includeVdr: sourceFlags.includeVdr,
        includeIrl: sourceFlags.includeIrl,
        includeObservations: sourceFlags.includeObservations,
        irlTags: sourceFlags.irlTags,
        observationTags: sourceFlags.observationTags,
        fileIds: noneSelected ? [] : fileIds?.length ? fileIds : null,
        folderIds: folderIds?.length ? folderIds : null,
        customContext: cloneCustomContext(config),
        label: 'Custom context',
    };
}

export function validateCustomContext(config, allWorkstreamIds = []) {
    const errors = [];
    if (!config) return ['Configure custom context before applying.'];

    const anyEnabled = CUSTOM_SOURCE_KEYS.some(key => config[key]?.enabled);
    if (!anyEnabled) {
        errors.push('Enable at least one source (Documents, IRL, or Observations).');
    }

    if (
        config.documents?.enabled &&
        !getCustomSourceWorkstreamIds(config, 'documents', allWorkstreamIds).length
    ) {
        errors.push('Select at least one workstream for Documents (VDR).');
    }
    if (config.irl?.enabled && !getCustomSourceWorkstreamIds(config, 'irl', allWorkstreamIds).length) {
        errors.push('Select at least one workstream for IRL Responses.');
    }
    if (
        config.observations?.enabled &&
        !getCustomSourceWorkstreamIds(config, 'observations', allWorkstreamIds).length
    ) {
        errors.push('Select at least one workstream for Observations.');
    }

    if (!getUnionCustomWorkstreamIds(config, allWorkstreamIds).length) {
        errors.push('Select workstreams for at least one enabled source.');
    }

    return errors;
}

export function formatCustomContextSelectionSummary(config, counts = {}) {
    const parts = [];
    if (config?.documents?.enabled && counts.documents != null) {
        parts.push(`${counts.documents} file${counts.documents === 1 ? '' : 's'}`);
    }
    if (config?.irl?.enabled && counts.irlResponses != null) {
        parts.push(`${counts.irlResponses} IRL`);
    }
    if (config?.observations?.enabled && counts.observations != null) {
        parts.push(`${counts.observations} observation${counts.observations === 1 ? '' : 's'}`);
    }
    return parts.length ? parts.join(' · ') : 'Custom context';
}
